import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, FileDown, Printer } from "lucide-react";
import { toast } from "react-toastify";
import Button from "src/components/ui/Button";
import PurchaseInvoiceDocument from "src/components/invoice/PurchaseInvoiceDocument";
import publicPurchaseInvoiceApis from "src/api/publicPurchaseInvoice";
import { generatePDF } from "src/utils/pdfGenerator";

const PublicPurchaseInvoicePage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [tenant, setTenant] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const invoiceRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!invoiceId) return;
      try {
        setLoading(true);
        const res = await publicPurchaseInvoiceApis.getPublicPurchaseInvoice(
          invoiceId
        );
        if (!res?.data?.order) {
          setNotFound(true);
          return;
        }
        setOrder(res.data.order);
        setTenant(res.data.tenant);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [invoiceId]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await generatePDF(invoiceRef, `purchase-${order.purchaseOrderId}.pdf`);
    } catch (error) {
      toast.error("Failed to generate purchase order");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="animate-spin text-indigo-600" size={28} />
        <span className="ml-3 text-gray-700">Loading purchase order...</span>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">
          This purchase order link is invalid or no longer available.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <Helmet>
        <title>Purchase Order {order.purchaseOrderId} | Invyntra</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="no-print max-w-[210mm] mx-auto mb-4 flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handlePrint}
          className="flex items-center"
        >
          <Printer size={16} className="mr-2" /> Print PDF
        </Button>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-indigo-600 text-white flex items-center"
        >
          {downloading ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" /> Generating...
            </>
          ) : (
            <>
              <FileDown size={16} className="mr-2" /> Download PDF
            </>
          )}
        </Button>
      </div>

      <PurchaseInvoiceDocument ref={invoiceRef} order={order} tenant={tenant} />
    </div>
  );
};

export default PublicPurchaseInvoicePage;
